import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { backendGet, backendPost, backendPut } from "@accredo/fe-shared/services/dataService.js";
import ClaimStatusBadge from "../../../components/ClaimStatusBadge.jsx";

const EDITABLE_FIELDS = [
  "class_name",
  "title",
  "description",
  "subject",
  "body",
  "days_until_reminder",
  "value",
  "send_to",
  "active",
];

function ReminderForm({ reminder, onChange, states }) {
  const [reminds, setReminds] = useState([]);
  const selected_remind = reminds.find(
    (remind) => remind.class_name === reminder.class_name
  );

  useEffect(() => {
    backendPost("data/reminders/reminds", { label: "Erinnerungstypen" }).then(
      (response) => response?.data && setReminds(response.data)
    );
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" gutterBottom>
          Einstellungen
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(reminder.active)}
              onChange={(e) => onChange("active", e.target.checked)}
            />
          }
          label="Aktiv"
        />
        <TextField
          label="Titel"
          fullWidth
          margin="normal"
          value={reminder.title ?? ""}
          onChange={(e) => onChange("title", e.target.value)}
        />
        <TextField
          label="Beschreibung"
          fullWidth
          margin="normal"
          multiline
          rows={3}
          value={reminder.description ?? ""}
          onChange={(e) => onChange("description", e.target.value)}
        />
        <Box mt={2} mb={1}>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Box>
            <ClaimStatusBadge
              status={reminder.value}
              label={states[reminder.value] ?? reminder.value}
            />
          </Box>
        </Box>
        <TextField
          label="Intervall (Tage bis zur Erinnerung)"
          fullWidth
          margin="normal"
          type="number"
          value={reminder.days_until_reminder ?? ""}
          onChange={(e) => onChange("days_until_reminder", e.target.value)}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="reminder-send-to-label">Wird versendet an</InputLabel>
          <Select
            labelId="reminder-send-to-label"
            label="Wird versendet an"
            value={reminder.send_to ?? ""}
            onChange={(e) => onChange("send_to", e.target.value)}
          >
            <MenuItem value="kunde">Kunde</MenuItem>
            <MenuItem value="mitarbeiter">Mitarbeiter</MenuItem>
            <MenuItem value="versicherer">Versicherer</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="reminder-class-label">Erinnerungstyp</InputLabel>
          <Select
            labelId="reminder-class-label"
            label="Erinnerungstyp"
            value={reminder.class_name ?? ""}
            onChange={(e) => onChange("class_name", e.target.value)}
          >
            {reminds.map((remind) => (
              <MenuItem key={remind.class_name} value={remind.class_name}>
                {remind.name || remind.class_name}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" mt={1}>
            {selected_remind?.description ?? "Keine Beschreibung verfügbar"}
          </Typography>
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" gutterBottom>
          Nachricht
        </Typography>
        <TextField
          label="Betreff"
          fullWidth
          margin="normal"
          value={reminder.subject ?? ""}
          onChange={(e) => onChange("subject", e.target.value)}
        />
        <TextField
          label="Inhalt"
          fullWidth
          margin="normal"
          multiline
          rows={6}
          value={reminder.body ?? ""}
          onChange={(e) => onChange("body", e.target.value)}
        />
        <Box>
          {selected_remind?.input_schema &&
            Object.entries(selected_remind.input_schema).map(([key, value]) => (
              <Button
                key={key}
                size="small"
                variant="outlined"
                sx={{ mr: 1, mb: 1 }}
                onClick={() =>
                  onChange("body", `${reminder.body ?? ""} {{${key}}}`)
                }
              >
                {value.label}
              </Button>
            ))}
        </Box>
      </Grid>
    </Grid>
  );
}

function ReminderPreview({ reminder }) {
  const input_schema = reminder.input_schema;
  const [input_values, setInputValues] = useState(() =>
    Object.fromEntries(
      Object.entries(input_schema ?? {}).map(([key, schema]) => [
        key,
        schema.default || schema.example || "",
      ])
    )
  );
  const [preview, setPreview] = useState({ subject: "", body: "" });

  const fetchPreview = useCallback(async () => {
    const response = await backendPost("data/reminders/preview", {
      label: "Vorschau",
      toSend: { reminder_id: reminder.id, data: input_values },
    });
    if (response) setPreview(response);
  }, [reminder.id, input_values]);

  useEffect(() => {
    if (Object.keys(input_values).length === 0) return undefined;
    const timeout = setTimeout(fetchPreview, 300);
    return () => clearTimeout(timeout);
  }, [fetchPreview, input_values]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" gutterBottom>
          Standard-/Beispielwerte
        </Typography>
        {Object.keys(input_schema ?? {}).map((key) => (
          <TextField
            key={key}
            label={key}
            fullWidth
            margin="normal"
            value={input_values[key] ?? ""}
            onChange={(e) =>
              setInputValues((prev) => ({ ...prev, [key]: e.target.value }))
            }
          />
        ))}
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" gutterBottom>
          Vorschau
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Basiert auf dem gespeicherten Stand der Erinnerung.
        </Typography>
        <Typography variant="subtitle1" mt={1}>
          Betreff:
        </Typography>
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {preview.subject}
        </Typography>
        <Typography variant="subtitle1" mt={2}>
          Inhalt:
        </Typography>
        <Typography sx={{ whiteSpace: "pre-wrap" }}>{preview.body}</Typography>
      </Grid>
    </Grid>
  );
}

// Standard-Erinnerungstyp (Remind), wie ihn auch der ReminderRunner als Fallback nutzt
const DEFAULT_REMIND_CLASS = "App\\Domain\\Claim\\Reminder\\Remind";

/**
 * Bearbeiten einer Erinnerung bzw. Erstellen einer neuen für `new_status` (Einstellungen und Vorschau) aus dem Status-Workflow heraus.
 */
export default function ReminderEditModal({
  reminder_id,
  new_status,
  states,
  onClose,
  onSaved,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const is_new = !reminder_id;
  const [reminder, setReminder] = useState(() =>
    is_new
      ? {
          class_name: DEFAULT_REMIND_CLASS,
          title: "",
          description: "",
          subject: "",
          body: "",
          days_until_reminder: 14,
          value: new_status,
          send_to: "kunde",
          active: true,
        }
      : null
  );
  const [tab, setTab] = useState(0);
  const [is_saving, setIsSaving] = useState(false);

  useEffect(() => {
    if (is_new) return;
    backendGet(`data/reminders/${reminder_id}`, {
      label: "Erinnerung",
      enqueueSnackbar,
      setResult: (res) => setReminder(res.data),
    });
  }, [is_new, reminder_id, enqueueSnackbar]);

  const handleSave = async () => {
    const save = is_new
      ? (url, options) => backendPost(url, options)
      : (url, options) => backendPut(url, options);
    const res = await save(
      is_new ? "data/reminders" : `data/reminders/${reminder_id}`,
      {
        label: "Erinnerung",
        toSend: Object.fromEntries(
          EDITABLE_FIELDS.map((field) => [field, reminder[field]])
        ),
        enqueueSnackbar,
        setIsLoading: setIsSaving,
      }
    );
    if (res) {
      enqueueSnackbar(
        is_new ? "Erinnerung erstellt" : "Erinnerung gespeichert",
        {
          variant: "success",
        }
      );
      onSaved();
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {is_new ? "Erinnerung erstellen" : "Erinnerung bearbeiten"}
      </DialogTitle>
      <DialogContent dividers>
        {reminder && (
          <>
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
              <Tab label="Einstellungen" />
              <Tab label="Vorschau" disabled={is_new} />
            </Tabs>
            <Box pt={2}>
              {tab === 0 ? (
                <ReminderForm
                  reminder={reminder}
                  states={states}
                  onChange={(field, value) =>
                    setReminder((prev) => ({ ...prev, [field]: value }))
                  }
                />
              ) : (
                <ReminderPreview reminder={reminder} />
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            !reminder ||
            is_saving ||
            (is_new && !(reminder.title && reminder.subject && reminder.body))
          }
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ReminderEditModal.propTypes = {
  reminder_id: PropTypes.number,
  new_status: PropTypes.string,
  states: PropTypes.objectOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
